/**
 * Holding Detail Page Route
 *
 * Displays detailed information about a specific mutual fund holding
 * using the HoldingDetailScreen component.
 *
 * Supports URL search params for tab state:
 * - ?tab=performance (default)
 * - ?tab=rating
 * - ?tab=info
 * - ?tab=transactions
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  HoldingDetailScreen,
  type HoldingDetailTab,
} from "@/components/holding-detail";
import { GlobalErrorFallback } from "@/components/error";

// Valid tab values
const VALID_TABS: readonly HoldingDetailTab[] = [
  "performance",
  "rating",
  "info",
  "transactions",
] as const;

// Search params type
export interface HoldingDetailSearch {
  tab?: HoldingDetailTab;
}

/**
 * Validate and parse search params
 */
function validateSearch(search: Record<string, unknown>): HoldingDetailSearch {
  const tab = search.tab;

  // Validate tab is a valid value, otherwise return undefined (will use default)
  if (typeof tab === "string" && VALID_TABS.includes(tab as HoldingDetailTab)) {
    return { tab: tab as HoldingDetailTab };
  }

  return {};
}

export const Route = createFileRoute("/holdings/$holdingId")({
  component: HoldingDetailPage,
  validateSearch,
  errorComponent: GlobalErrorFallback,
});

function HoldingDetailPage() {
  const { holdingId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate({ to: "/holdings" });
  };

  const handleTabChange = (newTab: HoldingDetailTab) => {
    navigate({
      to: "/holdings/$holdingId",
      params: { holdingId },
      search: { tab: newTab },
      replace: true, // Replace history entry to avoid back button cycling through tabs
    });
  };

  return (
    <HoldingDetailScreen
      holdingId={holdingId}
      tab={tab}
      onTabChange={handleTabChange}
      onBack={handleBack}
      defaultTab="performance"
    />
  );
}
